import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import apiClient from '../../utils/axios';
import { goToAgent } from '../../utils/agentUrl';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const returnUrl = searchParams.get('returnUrl') || '';
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);

  const checkVerified = useCallback(async () => {
    if (!email) return;
    try {
      const res = await apiClient.get('/auth/verification-status', { params: { email } });
      setVerified(!!res.data?.verified);
    } catch {
      // 다음 확인 때 다시 시도
    }
  }, [email]);

  useEffect(() => {
    if (verified) return undefined;
    checkVerified();
    const t = setInterval(checkVerified, 5000);
    window.addEventListener('focus', checkVerified);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', checkVerified);
    };
  }, [verified, checkVerified]);

  const handleResend = async () => {
    if (!email) {
      setError('이메일 주소가 필요합니다.');
      return;
    }

    try {
      setResending(true);
      setError('');
      setMessage('');
      await apiClient.post('/auth/resend-verification', { email, returnUrl: returnUrl || undefined });
      setMessage('인증메일이 재전송되었습니다.');
    } catch (err) {
      setError(err.response?.data?.error || '인증 이메일 재전송에 실패했습니다.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Typography
          variant="subtitle1"
          sx={{ color: '#666666', fontWeight: 600, fontSize: '2.125rem', lineHeight: 1.235, textAlign: 'center', mb: 2 }}
        >
          PlanGo 인증서비스
        </Typography>
        <Paper
          elevation={0}
          sx={{
            padding: 4,
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#000000',
              marginBottom: 4,
              textAlign: 'center',
            }}
          >
            인증메일 전송 완료
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                marginBottom: 1,
                lineHeight: 1.8,
                textAlign: 'center',
              }}
            >
              {email ? (
                <>
                  <strong>{email}</strong>로 인증메일이 전송되었습니다.
                </>
              ) : (
                <>인증메일이 전송되었습니다.</>
              )}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                marginBottom: 1,
                lineHeight: 1.8,
                textAlign: 'center',
              }}
            >
              계정 생성을 완료하시려면, 인증메일 내 링크를 클릭해주세요.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                marginBottom: 1,
                lineHeight: 1.8,
                textAlign: 'center',
              }}
            >
              인증 링크의 유효시간은 1시간입니다.
            </Typography>
          </Box>

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 인증메일 재전송 섹션 */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: 2,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <EmailIcon sx={{ color: '#9e9e9e', fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#666666',
                  marginBottom: 0.5,
                }}
              >
                인증메일을 받지 못하셨나요?
              </Typography>
              <Button
                variant="text"
                onClick={handleResend}
                disabled={resending || !email}
                sx={{
                  color: '#000000',
                  padding: 0,
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {resending ? <CircularProgress size={16} /> : '인증메일 재전송'}
              </Button>
            </Box>
          </Box>

          {/* 로그인 화면으로 버튼 */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => goToAgent()}
            disabled={!verified}
            sx={{
              backgroundColor: '#000000',
              color: '#ffffff',
              py: 1.5,
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            로그인 화면으로
          </Button>
          {!verified && (
            <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', mt: 1.5 }}>
              이메일 인증을 완료하면 로그인할 수 있습니다.
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;

